import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, Permission } from '../types/auth';
import api from '../api/axios';

interface AuthContextType extends AuthState {
    login: (token: string, user: User) => void;
    logout: () => void;
    hasPermission: (permissionName: string) => boolean;
    hasActiveSubscription: () => boolean;
    updateSubscriptionStatus: (hasSubscription: boolean) => void;
    checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: true,
    });
    const [subscriptionActive, setSubscriptionActive] = useState<boolean>(true);

    const checkAuth = async () => {
        // Use sessionStorage for better security (token cleared on browser close)
        const token = sessionStorage.getItem('token');
        if (!token) {
            setState((prev: AuthState) => ({ ...prev, isLoading: false }));
            return;
        }

        try {
            // Validate token and get fresh user data
            const res = await api.get('/me');
            const user = res.data.data.user;
            const tenant = res.data.data.tenant;

            // Merge tenant into user object if needed or just use the updated structure
            if (tenant) {
                user.tenant = tenant;
            }

            // Update session storage
            sessionStorage.setItem('user', JSON.stringify(user));

            // Check subscription
            const hasSub = user.tenant?.has_active_subscription ?? true;
            sessionStorage.setItem('hasSubscription', hasSub ? 'true' : 'false');
            setSubscriptionActive(hasSub);

            setState({
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
            });
        } catch (error) {
            console.error("Auth check failed:", error);
            // If API fails (401), logout
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('hasSubscription');
            setState({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
            });
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    const login = (token: string, user: User) => {
        // Use sessionStorage for better security (token cleared on browser close)
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        // Check if user has active subscription (from tenant data if available)
        const hasSub = user.tenant?.has_active_subscription ?? true;
        sessionStorage.setItem('hasSubscription', hasSub ? 'true' : 'false');
        setSubscriptionActive(hasSub);
        setState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
        });
    };

    const logout = () => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('hasSubscription');
        setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
        });
    };

    const hasPermission = (permissionName: string): boolean => {
        // Super admin and admin have all permissions
        if (state.user?.role === 'super_admin' || state.user?.role === 'admin') {
            return true;
        }
        // Sub-admin checks assigned permissions
        return state.user?.permissions?.some((p: Permission) => p.name === permissionName) || false;
    };

    const hasActiveSubscription = (): boolean => {
        // Super admin doesn't need subscription
        if (state.user?.role === 'super_admin') {
            return true;
        }
        return subscriptionActive;
    };

    const updateSubscriptionStatus = (hasSubscription: boolean) => {
        sessionStorage.setItem('hasSubscription', hasSubscription ? 'true' : 'false');
        setSubscriptionActive(hasSubscription);
    };

    return (
        <AuthContext.Provider value={{ ...state, login, logout, hasPermission, hasActiveSubscription, updateSubscriptionStatus, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
